import { useAudio } from '@/hooks/use-audio';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

/**
 * Keeps audio playback smooth across route transitions on Firefox/Safari.
 *
 * Those engines let the media element's ~4Hz `timeupdate` event loop block
 * React's rendering commit during a route change, causing jank. Rather than
 * pausing the audio (which caused an audible stop/start and a Play↔Pause icon
 * flicker on every navigation — EVE-13083), this suspends the `timeupdate`-
 * driven `timingState` updates for the duration of the transition via
 * `setWillSkipTimingUpdate`. The audio keeps playing uninterrupted; the
 * scrubber simply freezes for the transition and resumes on the next tick.
 *
 * No-op on Chromium-based browsers (Chrome, Edge, Opera, Brave): this hook is
 * only mounted for non-Chromium engines (see `NavigationAudioSync` in
 * `_app`), so the skip flag is never set there and timing updates are
 * unaffected.
 *
 * `pendingRoutesRef` survives effect re-runs (the router object reference
 * changes each navigation) and handles overlapping transitions: the flag only
 * clears once every in-flight route change has settled.
 */
export function useNavigationAudioSync() {
    const router = useRouter();
    const { setWillSkipTimingUpdate } = useAudio();
    const pendingRoutesRef = useRef(0);

    useEffect(() => {
        const onStart = (
            _url: string,
            { shallow }: { shallow: boolean } = { shallow: false }
        ) => {
            if (shallow) return;
            pendingRoutesRef.current++;
            setWillSkipTimingUpdate(true);
        };

        const onDone = () => {
            pendingRoutesRef.current = Math.max(
                0,
                pendingRoutesRef.current - 1
            );
            if (pendingRoutesRef.current === 0) {
                setWillSkipTimingUpdate(false);
            }
        };

        router.events.on('routeChangeStart', onStart);
        router.events.on('routeChangeComplete', onDone);
        router.events.on('routeChangeError', onDone);
        return () => {
            router.events.off('routeChangeStart', onStart);
            router.events.off('routeChangeComplete', onDone);
            router.events.off('routeChangeError', onDone);
            // Never leave timing updates suspended if we unmount mid-transition.
            pendingRoutesRef.current = 0;
            setWillSkipTimingUpdate(false);
        };
    }, [router, setWillSkipTimingUpdate]);
}
